import { useEffect, useState } from 'react';
import { Button, Checkbox, Dialog, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import { AppActions } from 'src/store/AppReducer';
import moment from 'moment';
import ProjectSelect from './ProjectSelect';
import { withStyles } from '@mui/styles';
import { TableHeaderCell } from 'src/components/TableHeaderCell';
import { TableColumnCell } from 'src/components/TableColumnCell';
import { createTask, getAllTemplateTasks, getTaskTypes } from 'src/services/timeManagementService';
import SnackBar from 'src/components/SnackBar/SnackBar';
import { RootStateType } from 'src/store/RootReducer';
import { TTaskType } from 'src/models/timesheet';

interface TaskTemplatesProps {
  open: boolean;
  toggle: () => void;
  getData: () => void;
  projectsList:any;
  projectId: any;
}

const TaskTemplates: React.FC<TaskTemplatesProps> = ({ open, toggle, getData, projectsList, projectId }) => {
  const dispatch = useDispatch();
  const appUser = useSelector((state: RootStateType) => state.appReducer?.user);

  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('');
  const [snackBarMessage, setSnackBarMessage] = useState('');
  const [project, setProject] = useState('');
  const [checkAll, setCheckAll] = useState(false);
  const [checkList, setCheckList] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [taskTypeList, setTaskTypeList] = useState<TTaskType[]>([]); 

  const getTasks = () => {
    dispatch(AppActions.showLoader());
    getAllTemplateTasks().then((res) => {
      const data = res?.data?.data
      setTasks(data?.templateTasksSummery);
      dispatch(AppActions.hideLoader());
    })
    .catch((error) => {
      dispatch(AppActions.hideLoader());
      setSnackBarMessage(error?.request?.response ? error?.request?.response : 'Unable to load template tasks');
      setSnackbarSeverity('error');
      setSnackBarOpen(true);
    });
  }

  const getTaskApiHandler = () => {
    getTaskTypes()
      .then((res) => {
        if (res.data.statusCode === 200) {
          const data: TTaskType[] = res.data?.data?.taskTypes || [];
          const modifyTypes: TTaskType[] = data.filter(
            (item: TTaskType) => item.typeName !== 'COMMON'
          );
          setTaskTypeList(modifyTypes);
        }
      })
      .catch((err) => {});
  };

  //create task
  const onSubmitToCreate = () => {
    if(checkList.length > 0){
      dispatch(AppActions.showLoader());

      let payload = []
      for(let i=0; i < checkList.length; i++){
        const item = {
          projectId: project,
          endDate: null,
          startDate: moment(new Date()).format('yyyy-MM-DD'),
          createdBy: appUser.EmployeeId,
          taskName: checkList[i].taskName,
          taskTypeId: checkList[i].taskTypeId,
          status: checkList[i].status,
          templateTaskId: checkList[i].id,
          referenceId: null
        }
        payload.push(item);
      }

      createTask(payload).then((res) => {
        if (res.data?.statusCode === 403) {
          setSnackBarMessage(res.data?.message ? res.data?.message : 'Unable to create activity');
          setSnackbarSeverity('error');
          setSnackBarOpen(true);
          setCheckAll(false);
          setCheckList([]);
          dispatch(AppActions.hideLoader());
        } else {
          setSnackBarMessage(res.data?.message ? res.data?.message : 'You’ve created the activity successfully.');
          setSnackbarSeverity('success');
          setSnackBarOpen(true);
          setCheckAll(false);
          setCheckList([]);
          getData();
        }
        
        /* toggle(); */
      })
      .catch((error) => {
        dispatch(AppActions.hideLoader());
        setSnackBarMessage(error?.request?.response ? error?.request?.response : `Unable to add ${checkList.length > 1 ? 'activities' : 'activity'}`);
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
      });
    }    
  }

  const getTaskType = (value: number) => {
    let type;
  
    if (taskTypeList.length > 0) {
      const foundType = taskTypeList.find((item) => item.id === value);
      type = foundType ? foundType.typeName : '';
    }
  
    return type;
  };

  useEffect(() => {
    if(open){
      setCheckAll(false);
      setCheckList([]);
    }
  }, [open]);

  useEffect(() => {
    getTasks();
    getTaskApiHandler();
  }, []);

  useEffect(() => {
    if(projectsList.length > 0){
      setProject(projectsList[0]?.projectId)
    }
  }, [projectsList]);

  useEffect(() => {
    setProject(projectId)
  }, [projectId]);

  // custom table row with different color
  const CustomTableRow = withStyles({
    root: {
      background: '#E6E9FF !important',
      height: '3rem'
    }
  })(TableRow);

  return (
    <Dialog open={open} maxWidth="sm" fullWidth scroll="paper" className='task-templates'
      onClose={() => {
        /* if(projectsList.length > 0){
          setProject(projectsList[0]?.projectId)
        } */
        toggle();
      }}>
      <Grid item xs={12} sx={{ p: 3, pb: 2 }} display='flex' justifyContent='space-between'>
        <Typography style={{ fontSize: '14px', fontWeight: '700' }} variant="h4" >
          Load from Template
        </Typography>
        <CloseIcon 
          onClick={() => {
            /* if(projectsList.length > 0){
              setProject(projectsList[0]?.projectId)
            } */
            toggle();
          }}
          style={{
            fontSize: '17px',
            cursor: 'pointer'
          }}/>
      </Grid>
      <Grid item xs={12} sx={{ p: 3, pt: 0 }} gap={1}>
        <Grid item xs={12} pb='1rem'>
          <ProjectSelect
            disabled={true}
            list={projectsList}
            value={project}
            onChange={(e: any) => {
              setProject(e.target.value);
            }}/>
        </Grid>
        <TableContainer className="tableContainer" >
          <Table style={{ borderCollapse: 'separate', position: 'relative' }} size="small">
            <TableHead>
              <CustomTableRow style={{position: 'sticky', top: 0, zIndex: 1}}>
                <TableCell
                  style={{
                    width: '30px',
                    whiteSpace: 'nowrap',
                    alignItems: 'center',
                    color: '#232C68',
                    fontWeight: '700',
                    fontSize: '11px',
                    background: '#E6E9FF',
                    paddingRight: 0
                  }}>
                  <Checkbox
                    className='check-box' 
                    indeterminate={
                      checkList.length > 0 &&
                      checkList.length <
                      tasks.length
                    }
                    checked={checkAll} 
                    onChange={(e) => {
                      if(e.target.checked){
                        setCheckAll(true);
                        setCheckList(tasks);
                      } else{
                        setCheckAll(false);
                        setCheckList([]);
                      }
                    }}/>
                </TableCell>
                <TableHeaderCell content="ID" smallFont/>
                <TableHeaderCell width='300px' content="Activity Name" smallFont/>
                <TableHeaderCell content="Activity Type" smallFont/>
              </CustomTableRow>
            </TableHead>
            <TableBody>
              {tasks &&
                tasks?.length > 0 &&
                tasks.map((task, i) => {
                  return (
                    <TableRow 
                      className={checkList.map(e => e.id).includes(task.id) ? 'selected-row' : ''}
                      hover 
                      key={task.id}>
                      <TableColumnCell>
                        <Grid display='flex' alignItems='center' justifyContent='flex-start' gap={1}>
                          <Checkbox 
                            className='check-box'
                            checked={checkList.filter(e => e.id === task.id).length > 0 ? true : false}
                            onChange={(e) => {
                              let newCheckList = []
                              if(e.target.checked){
                                newCheckList = [...checkList, task];
                                setCheckAll(newCheckList.length === tasks.map(e => e.id).length ? true : false);
                              } else {
                                setCheckAll(false);
                                newCheckList = checkList.filter(e => e.id !== task.id);
                              }
                              setCheckList(newCheckList);
                            }}/>
                        </Grid>
                      </TableColumnCell>
                      <TableColumnCell>
                        <Typography
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          variant="body2"
                          style={{ fontSize: '11px' }}
                          gutterBottom
                          noWrap
                        >
                          {task.id}
                        </Typography>
                      </TableColumnCell>
                      <TableColumnCell>
                        <Typography
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          variant="body2"
                          style={{ fontSize: '11px' }}
                          gutterBottom
                          noWrap
                        >
                          {task.taskName}
                        </Typography>
                      </TableColumnCell>
                      <TableColumnCell>
                        <Typography
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          variant="body2"
                          style={{ fontSize: '11px' }}
                          gutterBottom
                          noWrap
                        >
                          {getTaskType(task.taskTypeId)}
                        </Typography>
                      </TableColumnCell>
                    </TableRow>
                  );
                })
              }
            </TableBody>
          </Table>
        </TableContainer>
        <Grid item sx={{display: 'flex', justifyContent: 'flex-end', mt: 3 }} xs={12}>            
          <Button
            id="btn-onClose"
            sx={{
              width: '80px',
              fontWeight: 'normal',
              background: '#F5F7FF',
              fontSize: '13px'
            }}
            onClick={() => {
              /* if(projectsList.length > 0){
                setProject(projectsList[0]?.projectId)
              } */
              toggle()
            }}
          >
            Cancel
          </Button>
          <Button
            id="btn-onSubmit"
            sx={{
              ml: 2,
              width: '180px',
              fontSize: '13px',
              fontWeight: 'normal',
              background: '#0069E4',
            }}
            variant="contained"
            type="submit"
            onClick={() => {
              onSubmitToCreate()
            }}
          >
            + Add to the Project
          </Button>
        </Grid>
      </Grid>
      <SnackBar
        snackBarOpen={snackBarOpen}
        setSnackBarOpen={setSnackBarOpen}
        snackBarMessage={snackBarMessage}
        snackbarSeverity={snackbarSeverity}
      />
    </Dialog>
  );
};

export default TaskTemplates;
